import React, { useState } from 'react';
import { Plus, Edit, Trash2, X, DollarSign, Calendar, ToggleLeft, ToggleRight, Star } from 'lucide-react';
import styles from './CSS/PaymentPlans.module.css';
import { 
  useAdminPlans, 
  useCreatePlan, 
  useUpdatePlan, 
  useDeletePlan, 
  useTogglePlanStatus,
  useTogglePlanPopular
} from '../../../hooks/admin/useAdminPaymentPlans';

const PaymentPlans = () => {
  const { plans, isLoading, refetchPlans } = useAdminPlans();
  const createPlanMutation = useCreatePlan();
  const updatePlanMutation = useUpdatePlan();
  const deletePlanMutation = useDeletePlan();
  const togglePlanMutation = useTogglePlanStatus();
  const togglePopularMutation = useTogglePlanPopular();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    billing_cycle: 'monthly',
    description: '',
    is_active: true,
    popular: false
  });

  const openModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        price: plan.price.toString(),
        billing_cycle: plan.billing_cycle,
        description: plan.description || '',
        is_active: plan.is_active,
        popular: plan.popular
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        price: '',
        billing_cycle: 'monthly',
        description: '',
        is_active: true,
        popular: false
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPlan(null);
    setFormData({
      name: '',
      price: '',
      billing_cycle: 'monthly',
      description: '',
      is_active: true,
      popular: false
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      price: parseFloat(formData.price)
    };

    try {
      if (editingPlan) {
        await updatePlanMutation.mutateAsync({ id: editingPlan.id, planData: submitData });
      } else {
        await createPlanMutation.mutateAsync(submitData);
      }
      closeModal();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleDelete = async (planId) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        await deletePlanMutation.mutateAsync(planId);
      } catch (error) {
        console.error('Error deleting plan:', error);
      }
    }
  };

  const handleToggleStatus = async (planId) => {
    try {
      await togglePlanMutation.mutateAsync(planId);
    } catch (error) {
      console.error('Error toggling plan status:', error);
    }
  };

  const handleTogglePopular = async (planId) => {
    try {
      await togglePopularMutation.mutateAsync(planId);
    } catch (error) {
      console.error('Error toggling plan popular status:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Payment Plans Management</h1>
          <p className={styles.subtitle}>
            Create and manage subscription plans for your users
          </p>
        </div>
        <button 
          className={styles.addButton}
          onClick={() => openModal()}
        >
          <Plus className={styles.addIcon} />
          Add New Plan
        </button>
      </div>

      {plans.length === 0 ? (
        <div className={styles.emptyState}>
          <DollarSign className={styles.emptyStateIcon} />
          <div className={styles.emptyStateText}>No payment plans found</div>
          <div className={styles.emptyStateSubtext}>
            Click the "Add New Plan" button to create your first subscription plan
          </div>
        </div>
      ) : (
        <div className={styles.plansGrid}>
          {plans.map((plan) => (
            <div key={plan.id} className={`${styles.planCard} ${!plan.is_active ? styles.inactive : ''}`}>
              <div className={styles.planHeader}>
                <div className={styles.planTitle}>
                  <h3>{plan.name}</h3>
                  <span className={styles.planPrice}>{formatPrice(plan.price)}</span>
                </div>
                <div className={styles.planStatus}>
                  <button
                    className={`${styles.statusToggle} ${plan.is_active ? styles.active : styles.inactive}`}
                    onClick={() => handleToggleStatus(plan.id)}
                    title={plan.is_active ? 'Deactivate Plan' : 'Activate Plan'}
                  >
                    {plan.is_active ? (
                      <ToggleRight className={styles.toggleIcon} />
                    ) : (
                      <ToggleLeft className={styles.toggleIcon} />
                    )}
                  </button>
                  <button
                    className={`${styles.popularToggle} ${plan.popular ? styles.popular : styles.notPopular}`}
                    onClick={() => handleTogglePopular(plan.id)}
                    title={plan.popular ? 'Unmark as Popular' : 'Mark as Popular'}
                  >
                    <Star className={styles.starIcon} />
                  </button>
                </div>
              </div>

              <div className={styles.planDetails}>
                <div className={styles.detailItem}>
                  <Calendar className={styles.detailIcon} />
                  <span>{plan.billing_cycle}</span>
                </div>
                {plan.description && (
                  <div className={styles.planDescription}>
                    {plan.description}
                  </div>
                )}
                <div className={styles.planMeta}>
                  <span>Created: {formatDate(plan.created_at)}</span>
                </div>
              </div>

              <div className={styles.planActions}>
                <button 
                  className={`${styles.actionButton} ${styles.editButton}`}
                  onClick={() => openModal(plan)}
                >
                  <Edit />
                  Edit
                </button>
                <button 
                  className={`${styles.actionButton} ${styles.deleteButton}`}
                  onClick={() => handleDelete(plan.id)}
                >
                  <Trash2 />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className={styles.modal} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {editingPlan ? 'Edit Plan' : 'Add New Plan'}
              </h2>
              <button className={styles.closeButton} onClick={closeModal}>
                <X />
              </button>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Plan Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={styles.formInput}
                  required
                  placeholder="Enter plan name"
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Price *</label>
                  <div className={styles.priceInput}>
                    <span className={styles.currencySymbol}>₹</span>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      className={styles.formInput}
                      required
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Billing Cycle</label>
                  <select
                    name="billing_cycle"
                    value={formData.billing_cycle}
                    onChange={handleInputChange}
                    className={styles.formSelect}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="six_months">6 Months</option>
                    <option value="yearly">Yearly</option>
                    <option value="weekly">Weekly</option>
                    <option value="daily">Daily</option>
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className={styles.formTextarea}
                  placeholder="Enter plan description (optional)"
                  rows="3"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Active Plan</span>
                </label>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="popular"
                    checked={formData.popular}
                    onChange={handleInputChange}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Popular Plan</span>
                </label>
              </div>

              <button 
                type="submit" 
                className={styles.submitButton}
                disabled={createPlanMutation.isLoading || updatePlanMutation.isLoading}
              >
                {createPlanMutation.isLoading || updatePlanMutation.isLoading
                  ? 'Saving...'
                  : editingPlan 
                    ? 'Update Plan' 
                    : 'Create Plan'
                }
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPlans; 